import { useConnectCredentialsStore } from '../../stores/session/connectCredentialsStore';
import { useSessionStore } from '../../stores/session/sessionStore';
import { useTenantStore } from '../../stores/tenantStore';
import { oidcUserManager } from '../../services/oidcService';
import { refreshAcceoConnectToken } from '../../services/acceoConnectService';

// Minimum token validity required before starting a long-running sync operation.
const MIN_VALIDITY_MS = 15 * 60 * 1000;

/**
 * Returns the current AcceoConnect token, refreshing it first if it is expired
 * or will expire within MIN_VALIDITY_MS (to avoid expiry mid-sync).
 * Throws if the session cannot be refreshed.
 */
export async function ensureFreshToken(): Promise<string> {
    const { expiresAt, acceoConnectToken, isExpired, portalIsToken, setAcceoConnectToken, setSaasSessionToken } = useConnectCredentialsStore.getState();
    const { sessionKind } = useSessionStore.getState();
    const { tenantCode } = useTenantStore.getState();

    // Portal sessions: the AcceoConnect token is managed server-side — the client neither
    // refreshes nor sends it. Return empty so the sync trigger omits the body token (the backend sources
    // it from the Connect session) and we never touch the possibly-expired host IS JWT. Desktop sessions
    // also carry an app refresh token now, so key off the session kind, not the token's presence.
    if (sessionKind === 'portal') return '';

    if (!acceoConnectToken) throw new Error('No AcceoConnect token.');

    const willExpireDuringSync = expiresAt !== null && expiresAt - Date.now() < MIN_VALIDITY_MS;
    if (!isExpired() && !willExpireDuringSync) return acceoConnectToken;

    if (!tenantCode) throw new Error('No tenant code for token refresh.');

    if (portalIsToken) {
        const result = await refreshAcceoConnectToken(portalIsToken, tenantCode);
        if (!result) throw new Error('Token refresh returned null.');
        setAcceoConnectToken(result.access_token, result.expires_in);
        setSaasSessionToken(result.saasSessionToken ?? null);
        return result.access_token;
    }

    const oidcUser = await oidcUserManager.getUser();
    if (!oidcUser || oidcUser.expired || !oidcUser.access_token) {
        try {
            await oidcUserManager.signinRedirect();
        } catch (e) {
            console.error('signinRedirect failed:', e);
        }
        throw new Error('OIDC session unavailable.');
    }

    const result = await refreshAcceoConnectToken(oidcUser.access_token, tenantCode);
    if (!result) throw new Error('Token refresh returned null.');
    setAcceoConnectToken(result.access_token, result.expires_in);
    setSaasSessionToken(result.saasSessionToken ?? null);
    return result.access_token;
}
